import { useEffect, useState } from "react";
import { createInventoryTransaction, getOrderReceipt } from "../../services/OnlineStore";
import styles from "./PaymentSuccess.module.css";

function getStoredPaymentData() {
  const storedPaymentData = sessionStorage.getItem("paymentData");

  if (!storedPaymentData) {
    return null;
  }

  try {
    return JSON.parse(storedPaymentData);
  } catch (error) {
    console.error("Failed to parse payment data:", error);
    return null;
  }
}

function PaymentSuccess() {
  const [paymentData] = useState(getStoredPaymentData);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);

  useEffect(() => {
    sessionStorage.removeItem("paymentData");
  }, []);

  const payment = paymentData?.payment;
  const customer = paymentData?.customer;
  const order = paymentData?.order;
  const cartToken = paymentData?.cartToken ?? payment?.cart_token;
  const orderId = order?.id
    ?? order?.ID
    ?? order?.order_id
    ?? paymentData?.orderId
    ?? paymentData?.order_id
    ?? payment?.order_id
    ?? payment?.orderId;

  useEffect(() => {
    const customerId = customer?.id;
    const items = order?.items ?? [];

    if (!cartToken || !orderId) {
      console.error("Cannot create inventory transaction: missing cart token or order ID", {
        cartToken,
        orderId,
      });
      return undefined;
    }

    const transactionKey = `inventoryTransaction:${cartToken}:${orderId}`;
    if (sessionStorage.getItem(transactionKey)) {
      return undefined;
    }

    sessionStorage.setItem(transactionKey, "pending");
    const transactionPayload = {
      items,
      customer_id: customerId ?? 0,
      order_id: orderId,
    };

    console.log("Creating inventory transaction:", {
      cartToken,
      transactionPayload,
    });

    createInventoryTransaction(cartToken, transactionPayload)
      .then((response) => {
        console.log("Inventory transaction created:", response);
        sessionStorage.setItem(transactionKey, "completed");
      })
      .catch((error) => {
        sessionStorage.removeItem(transactionKey);
        console.error("Failed to create inventory transaction:", error);
      });

    return undefined;
  }, [cartToken, customer?.id, order?.items, orderId]);

  useEffect(() => {
    if (!orderId) {
      return undefined;
    }

    let cancelled = false;
    let prefetchedUrl = null;
    setReceiptLoading(true);
    setReceiptError(null);
    setReceiptUrl(null);

    getOrderReceipt(orderId)
      .then((receiptBlob) => {
        if (cancelled) {
          return;
        }

        prefetchedUrl = URL.createObjectURL(receiptBlob);
        setReceiptUrl(prefetchedUrl);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to prefetch order receipt:", error);
          setReceiptError("We could not load your receipt. Please try again.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReceiptLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (prefetchedUrl) {
        URL.revokeObjectURL(prefetchedUrl);
      }
    };
  }, [orderId]);

  useEffect(() => {
    return () => {
      if (receiptUrl) {
        URL.revokeObjectURL(receiptUrl);
      }
    };
  }, [receiptUrl]);

  const openReceipt = (url) => {
    const receiptLink = document.createElement("a");
    receiptLink.href = url;
    receiptLink.target = "_blank";
    receiptLink.rel = "noopener noreferrer";
    receiptLink.click();
  };

  const handleReceiptClick = async () => {
    if (!orderId) {
      setReceiptError("The order receipt is not available.");
      return;
    }

    if (receiptUrl) {
      openReceipt(receiptUrl);
      return;
    }

    setReceiptLoading(true);
    setReceiptError(null);

    try {
      const receiptBlob = await getOrderReceipt(orderId);
      const fallbackReceiptUrl = URL.createObjectURL(receiptBlob);
      setReceiptUrl(fallbackReceiptUrl);
      openReceipt(fallbackReceiptUrl);
    } catch (error) {
      console.error("Failed to retrieve order receipt:", error);
      setReceiptError("We could not load your receipt. Please try again.");
    } finally {
      setReceiptLoading(false);
    }
  };

  return (
    <main className={styles.page}>
      <section className={styles.content} aria-labelledby="payment-success-title">
        <h1 id="payment-success-title">Thank you!</h1>
        <button
          className={styles.receiptButton}
          type="button"
          onClick={handleReceiptClick}
          disabled={receiptLoading || !orderId}
        >
          {receiptLoading ? "Preparing receipt..." : "Keep the receipt"}
        </button>
        {receiptError && <p className={styles.error} role="alert">{receiptError}</p>}
      </section>
    </main>
  );
}

export default PaymentSuccess;